import { apiBPConfirm, apiForGoldConfirmation } from '@api/apiCall';
import BPTnCHeader from '@atoms/BPTnCHeader';
import { Text, View } from '@components/primitives';
import { AntDesign } from '@expo/vector-icons';
import { useToast } from '@hooks';
import BPTncAndBillDetails from '@molecules/BPTncAndBillDetails';
import navigationConstants from '@navigation/navigationConstants';
import { useNavigation } from '@react-navigation/native';
import { getStore } from '@store/storeUtils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import colors from '@theme/colors';
import AmplitudeHelper from '@utils/analytics';
import { AMPLITUDE_CONSTANTS } from '@utils/analytics/constants';
import { getAmplitudeKeyValue, showErrorOnAPI } from '@utils/utils';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Platform, TouchableOpacity } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { logErrorAnalytics, logSheetOpened, logTnCClosed, logTxClosedWithSource } from './analyticsRequestTxSheet';
import { PaymentMethod, RequestTransactionSheetProps } from './types';
import ModalHeader from '@components/primitives/ModalHeader';

const getMutationConfig = (isGold?: boolean) => ({
  mutationFn: (payload: any) => (isGold ? apiForGoldConfirmation(payload) : apiBPConfirm(payload)),
});

const logAmplitudeEvent = (
  eventName: any,
  productConfig: any,
  bpReqData: any,
  selectedBPAccountForConfirm: any,
  source: string,
  validatedReferralCode: string,
  metaParams: Record<string, any> = {},
) => {
  const { name, attributes } = eventName;
  AmplitudeHelper.logEvent(name, {
    [attributes.transaction_category_id]: productConfig?.transaction_category_id,
    [attributes.product_price]: bpReqData?.subtotal,
    [attributes.product_code]: bpReqData?.productCode,
    [attributes.reason]: productConfig?.reason,
    [attributes.reason_id]: productConfig?.reasonId,
    [attributes.payment_method]: selectedBPAccountForConfirm?.amplitudeAccountName,
    [attributes.source]: source,
    [attributes.fee_charged]: bpReqData?.fee,
    [attributes.referral_code]: validatedReferralCode,
    ...metaParams,
  });
};

const RequestTransactionSheet: React.FC<RequestTransactionSheetProps> = ({
  openRTS = false,
  setOpenRTS = () => {},
  bpReqData = {
    paymentMethods: [{ id: -1 }],
    productCode: '',
    subtotal: 0,
    fee: 0,
  },
  productConfig = {
    hasAccountInput: false,
    requestInfo: { header: [{ key: '', value: '' }] },
    transaction_category_id: '',
    reason: '',
    reasonId: 0,
  },
  productApiData = { prefix: '' },
  selectedAccountNumber = '',
  source = '',
  nominalSelected = { code: '' },
  isGold = false,
  errorHandler = () => {},
}) => {
  const {
    validatedReferralCode,
    setLoading,
    setInitialData,
    selectedBPAccount,
    setValidatedReferralCode,
    meAmplitudeContext,
  } = getStore();

  const [tncChecked, setTncChecked] = useState(false);
  const [tncOpen, setTncOpen] = useState(false);
  const navigation = useNavigation<any>();
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const [selectedBPAccountForConfirm, setSelectedBPAccountForConfirm] = useState<PaymentMethod>({});
  const toast = useToast();
  // Remove ref - control with index prop

  const { isLoading, isError, isSuccess, error, data, mutate: confirmBP } = useMutation(getMutationConfig(isGold));

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (isError && (error || isGold)) {
      logErrorAnalytics(productConfig, bpReqData, t, error, selectedBPAccountForConfirm, source);
    }
    showErrorOnAPI(isError, error, toast, t, navigation, setInitialData);
    if (isError && isGold) {
      errorHandler();
    }
  }, [isError, error, isGold]);

  useEffect(() => {
    if (isSuccess && data) {
      queryClient.invalidateQueries({ queryKey: ['transactions_history'] });
      setOpenRTS(false);

      const e1 = AMPLITUDE_CONSTANTS.BillPayment.selamatPage;
      const e2 = AMPLITUDE_CONSTANTS.BillPayment.BillPaymentSuccess;

      logAmplitudeEvent(e1, productConfig, bpReqData, selectedBPAccountForConfirm, source, validatedReferralCode);

      logAmplitudeEvent(
        e2,
        productConfig,
        bpReqData,
        selectedBPAccountForConfirm,
        source,
        validatedReferralCode,
        getAmplitudeKeyValue(e2.name, data?.amplitudeEventContext),
      );

      setValidatedReferralCode(null);
      navigation.navigate(navigationConstants.GG_BP_SUCCESS, {
        bpRequestSuccess: data,
        bpReqData,
        productConfig,
        selectedBPAccountForConfirm,
        source,
        productApiData,
      });
    }
  }, [isSuccess, data]);

  useEffect(() => {
    if (bpReqData?.paymentMethods?.length && selectedBPAccount?.id !== undefined) {
      const matched = bpReqData.paymentMethods.find(item => item.id === selectedBPAccount.id);
      if (matched) {
        setSelectedBPAccountForConfirm(matched);
      }
    }
  }, [bpReqData]);

  useEffect(() => {
    if (openRTS) {
      logSheetOpened(selectedAccountNumber, productConfig, bpReqData, source, validatedReferralCode);
    }
  }, [openRTS]);

  const closeRequestTxSheet = () => {
    if (tncOpen) {
      logTnCClosed(productConfig, source);
    } else {
      logTxClosedWithSource(productConfig, source, 'X');
    }

    setOpenRTS(false, true);
    setTncOpen(false);
    setTncChecked(false);
  };

  return (
    <Modal
      visible={openRTS}
      animationType="none"
      presentationStyle="overFullScreen"
      transparent={true}
      onRequestClose={closeRequestTxSheet}>
      <TouchableOpacity
        style={{
          flex: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          justifyContent: 'flex-end',
        }}
        activeOpacity={1}
        onPress={closeRequestTxSheet}>
        <TouchableOpacity
          style={{
            backgroundColor: 'white',
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            maxHeight: '80%',
            minHeight: '50%',
          }}
          activeOpacity={1}
          onPress={e => e.stopPropagation()}>
          {Platform.OS === 'ios' || Platform.OS === 'web' ? (
            <SafeAreaView edges={['bottom']} style={{ flex: 1 }}>
              <View style={{ borderBottomWidth: 1, borderBottomColor: '#eee' }}>
                {tncOpen ? (
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <BPTnCHeader />
                    <TouchableOpacity onPress={closeRequestTxSheet} style={{ padding: 10 }}>
                      <AntDesign name="close" size={20} color={colors.neutral.charcoal} />
                    </TouchableOpacity>
                  </View>
                ) : (
                  <ModalHeader title={t('reqTxSheet.chosenProduct')} onClose={closeRequestTxSheet} />
                )}
              </View>
              <View style={{ flex: 1 }}>
                <BPTncAndBillDetails
                  isLoading={isLoading}
                  tncOpen={tncOpen}
                  productConfig={productConfig}
                  props={{
                    openRTS,
                    setOpenRTS,
                    bpReqData,
                    productConfig,
                    productApiData,
                    selectedAccountNumber,
                    source,
                    nominalSelected,
                    isGold,
                    errorHandler,
                  }}
                  bpReqData={bpReqData}
                  tncChecked={tncChecked}
                  setOpenRTS={setOpenRTS}
                  validatedReferralCode={validatedReferralCode}
                  selectedBPAccount={selectedBPAccountForConfirm}
                  setTncOpen={setTncOpen}
                  setTncChecked={setTncChecked}
                  confirmBP={confirmBP}
                  setSelectedBPAccount={setSelectedBPAccountForConfirm}
                  source={source}
                  meAmplitudeContext={meAmplitudeContext}
                />
              </View>
            </SafeAreaView>
          ) : (
            <>
              <View style={{ borderBottomWidth: 1, borderBottomColor: '#eee' }}>
                {tncOpen ? (
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <BPTnCHeader />
                    <TouchableOpacity onPress={closeRequestTxSheet} style={{ padding: 10 }}>
                      <AntDesign name="close" size={20} color={colors.neutral.charcoal} />
                    </TouchableOpacity>
                  </View>
                ) : (
                  <ModalHeader title={t('reqTxSheet.chosenProduct')} onClose={closeRequestTxSheet} />
                )}
              </View>
              <View>
                <BPTncAndBillDetails
                  isLoading={isLoading}
                  tncOpen={tncOpen}
                  productConfig={productConfig}
                  props={{
                    openRTS,
                    setOpenRTS,
                    bpReqData,
                    productConfig,
                    productApiData,
                    selectedAccountNumber,
                    source,
                    nominalSelected,
                    isGold,
                    errorHandler,
                  }}
                  bpReqData={bpReqData}
                  tncChecked={tncChecked}
                  setOpenRTS={setOpenRTS}
                  validatedReferralCode={validatedReferralCode}
                  selectedBPAccount={selectedBPAccountForConfirm}
                  setTncOpen={setTncOpen}
                  setTncChecked={setTncChecked}
                  confirmBP={confirmBP}
                  setSelectedBPAccount={setSelectedBPAccountForConfirm}
                  source={source}
                  meAmplitudeContext={meAmplitudeContext}
                />
              </View>
            </>
          )}
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
};

export default RequestTransactionSheet;
